import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Settings, FileText, Palette, QrCode, Calendar, Plus, Ticket, Link as LinkIcon, Copy, ExternalLink, BarChart3 } from 'lucide-react';
import { useEvent } from '../context/EventContext';
import TicketingSettings from './TicketingSettings';
import CustomFields from './CustomFields';
import BadgeTemplates from './BadgeTemplates';
import StickerTemplates from './StickerTemplates';
import EventForm from './EventForm';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import EventSelector from '../components/common/EventSelector';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventAPI, registrationLinkAPI, shortUrlAPI } from '../api/endpoints';
import { useToastContext } from '../context/ToastContext';
import Loading from '../components/common/Loading';

// Inline component for Event Registration & Short Links
const EventLinksCard = () => {
  const { selectedEvent } = useEvent();
  const toast = useToastContext();
  const queryClient = useQueryClient();

  const { data: registrationLinkData } = useQuery({
    queryKey: ['registration-link', selectedEvent],
    queryFn: () => registrationLinkAPI.getLink(selectedEvent),
    enabled: !!selectedEvent && selectedEvent !== 'new',
  });

  const { data: shortUrlsData } = useQuery({
    queryKey: ['short-urls', selectedEvent],
    queryFn: () => shortUrlAPI.getAll(selectedEvent),
    enabled: !!selectedEvent && selectedEvent !== 'new',
  });

  const createShortUrlMutation = useMutation({
    mutationFn: () => shortUrlAPI.create(selectedEvent, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['short-urls', selectedEvent] });
      toast.success('Short link generated successfully!');
    },
    onError: () => {
      toast.error('Failed to generate short link');
    }
  });

  if (!selectedEvent || selectedEvent === 'new') return null;

  const normalUrl = registrationLinkData?.data?.registration_url || `${window.location.origin}/self-register?event=${selectedEvent}`;
  const shortUrls = shortUrlsData?.data || [];
  const activeShortUrlObj = shortUrls.find((u: any) => u.is_active && !u.is_expired) || shortUrls[0];
  const shortUrl = activeShortUrlObj?.short_url || '';

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch {
      toast.error(`Failed to copy ${label}`);
    }
  };

  return (
    <Card className="border border-teal-100 bg-gradient-to-r from-teal-50/50 via-emerald-50/30 to-white shadow-sm mb-6">
      <Card.Content className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-teal-100">
          <div>
            <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-teal-600" />
              Event Registration Links
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              Copy and share both standard and shortened registration links with your attendees
            </p>
          </div>
          {activeShortUrlObj && (
            <div className="flex items-center gap-2 text-xs font-medium text-teal-800 bg-teal-100/70 px-3 py-1.5 rounded-full self-start sm:self-auto">
              <BarChart3 className="h-3.5 w-3.5 text-teal-600" />
              <span>{activeShortUrlObj.click_count || 0} Total Clicks</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Normal Registration Link */}
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Standard Link
                </span>
                <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Full URL</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Direct link to full registration form</p>
              <input
                type="text"
                readOnly
                value={normalUrl}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-xs font-mono text-gray-800 select-all focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-xs gap-1.5 justify-center"
                onClick={() => copyToClipboard(normalUrl, 'Standard link')}
              >
                <Copy className="h-3.5 w-3.5 text-gray-600" />
                Copy Standard Link
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs gap-1.5"
                onClick={() => window.open(normalUrl, '_blank')}
              >
                <ExternalLink className="h-3.5 w-3.5 text-gray-600" />
                Open
              </Button>
            </div>
          </div>

          {/* Shortened Link */}
          <div className="bg-white p-4 rounded-xl border border-teal-200 shadow-sm flex flex-col justify-between space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold uppercase tracking-wider text-teal-700">
                  Shortened Link
                </span>
                <span className="text-[11px] bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded font-semibold">Professional Shortlink</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Clean, compact short URL for SMS, flyers & social posts</p>
              {shortUrl ? (
                <input
                  type="text"
                  readOnly
                  value={shortUrl}
                  className="w-full px-3 py-2 bg-teal-50/50 border border-teal-200 rounded-lg text-xs font-mono text-teal-900 font-semibold select-all focus:outline-none"
                />
              ) : (
                <div className="py-2 text-xs text-gray-400 italic">No short link generated yet</div>
              )}
            </div>
            <div className="flex items-center gap-2 pt-1">
              {shortUrl ? (
                <>
                  <Button
                    size="sm"
                    className="flex-1 text-xs gap-1.5 justify-center bg-teal-600 hover:bg-teal-700 text-white"
                    onClick={() => copyToClipboard(shortUrl, 'Shortened link')}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copy Short Link
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs gap-1.5 border-teal-200 text-teal-700 hover:bg-teal-50"
                    onClick={() => window.open(shortUrl, '_blank')}
                  >
                    <ExternalLink className="h-3.5 w-3.5 text-teal-600" />
                    Open
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  className="w-full text-xs gap-1.5 justify-center bg-teal-600 hover:bg-teal-700 text-white"
                  loading={createShortUrlMutation.isPending}
                  onClick={() => createShortUrlMutation.mutate()}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Generate Short Link
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

// Inline component for QR Flyer Settings
const QrPdfSettings = () => {
  const { selectedEvent, setSelectedEvent } = useEvent();
  const toast = useToastContext();
  const queryClient = useQueryClient();

  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: eventAPI.getAll,
  });

  const event = eventsData?.data?.events?.find((e: any) => e.id === selectedEvent);
  
  const [title, setTitle] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [showLink, setShowLink] = useState(true);
  const [guideText, setGuideText] = useState('');
  const [showGuideText, setShowGuideText] = useState(true);

  // Sync state with selected event
  useEffect(() => {
    if (event) {
      const pdfSettings = event.pdf_settings || {};
      setTitle(pdfSettings.title || '');
      setCtaText(pdfSettings.cta_text || '');
      
      const parseBool = (val: any, defaultVal: boolean) => {
        if (val === undefined || val === null) return defaultVal;
        if (val === false || val === 'false' || val === 0 || val === '0') return false;
        return true;
      };
      
      setShowLink(parseBool(pdfSettings.show_link, true));
      setGuideText(pdfSettings.guide_text || '');
      setShowGuideText(parseBool(pdfSettings.show_guide, true));
    }
  }, [event]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => eventAPI.update(selectedEvent, data),
    onSuccess: () => {
      toast.success('QR PDF Settings saved!');
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: () => {
      toast.error('Failed to save settings');
    }
  });

  const handleSave = () => {
    if (!selectedEvent) return;
    updateMutation.mutate({
      pdf_settings: {
        title: title.trim(),
        cta_text: ctaText.trim(),
        show_link: showLink,
        guide_text: guideText.trim(),
        show_guide: showGuideText
      }
    });
  };

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">QR Flyer Settings</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Customize the A4 printable QR code flyer</p>
          </div>
          <div className="w-full sm:w-auto">
            <EventSelector selectedEvent={selectedEvent} onEventChange={setSelectedEvent} />
          </div>
        </div>

        {isLoading ? (
          <Loading />
        ) : !selectedEvent ? (
          <Card>
            <Card.Content className="text-center py-12">
              <p className="text-gray-500">Select an event to manage its QR Flyer settings</p>
            </Card.Content>
          </Card>
        ) : (
          <Card>
            <Card.Content>
              <div className="space-y-6 max-w-2xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flyer Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={event?.name || "Event Name"}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank to use the event name</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Call to Action (CTA) Text
                  </label>
                  <input
                    type="text"
                    value={ctaText}
                    onChange={(e) => setCtaText(e.target.value)}
                    placeholder="Scan to Register"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">The text displayed immediately above the QR Code</p>
                </div>

                <div>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={showLink}
                      onChange={(e) => setShowLink(e.target.checked)}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Show Registration URL Link
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-7">Display the clickable link below the QR code for manual entry</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Guide Text
                  </label>
                  <textarea
                    value={guideText}
                    onChange={(e) => setGuideText(e.target.value)}
                    placeholder="Open your camera app and point it at the QR code to access the event registration page."
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Instructions for your attendees on how to scan the QR code.</p>
                </div>

                <div>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={showGuideText}
                      onChange={(e) => setShowGuideText(e.target.checked)}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Show Guide Text
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-7">Display the guide text instructions below the CTA.</p>
                </div>

                <div className="pt-4 flex justify-end">
                  <Button 
                    onClick={handleSave} 
                    loading={updateMutation.isPending}
                  >
                    Save Settings
                  </Button>
                </div>
              </div>
            </Card.Content>
          </Card>
        )}
      </div>
    </>
  );
};


const EventSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedEvent, setSelectedEvent } = useEvent();

  // Determine initial tab from URL hash, default to general
  const getInitialTab = () => {
    const hash = location.hash.replace('#', '');
    if (['general', 'registration', 'ticketing', 'qr-flyer', 'badges', 'stickers'].includes(hash)) {
      return hash;
    }
    return 'general';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());

  // Update URL hash when tab changes
  useEffect(() => {
    navigate(`#${activeTab}`, { replace: true });
  }, [activeTab, navigate]);

  // Sync state if URL changes externally
  useEffect(() => {
    setActiveTab(getInitialTab());
  }, [location.hash]);

  const allTabs = [
    { id: 'general', label: 'Event Details', icon: Calendar },
    { id: 'ticketing', label: 'Ticketing', icon: Ticket },
    { id: 'registration', label: 'Registration Form', icon: Settings },
    { id: 'qr-flyer', label: 'QR Flyer', icon: QrCode },
    { id: 'badges', label: 'Badge Templates', icon: FileText },
    { id: 'stickers', label: 'Sticker Templates', icon: Palette },
  ];

  const tabs = selectedEvent === 'new' 
    ? allTabs.filter(tab => tab.id === 'general') 
    : allTabs;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Top Level Tabs */}
        <div className="bg-white p-2 rounded-xl shadow-sm border border-gray-200 sticky top-[73px] z-10 mx-4 sm:mx-6 mt-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-teal-50 to-emerald-50 text-teal-700 shadow-sm border border-teal-100'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 border border-transparent'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
            {selectedEvent !== 'new' && (
              <button
                onClick={() => {
                  setSelectedEvent('new');
                  setActiveTab('general');
                }}
                className="ml-auto flex items-center gap-2 py-3 px-4 rounded-lg font-semibold text-sm text-primary-600 hover:bg-primary-50 transition-colors border border-transparent"
              >
                <Plus className="w-4 h-4" />
                Add Event
              </button>
            )}
          </div>
        </div>

        {/* Registration & Short Links Header Card */}
        {selectedEvent !== 'new' && (
          <div className="mx-4 sm:mx-6">
            <EventLinksCard />
          </div>
        )}

        {/* Tab Content */}
        <div>
          {activeTab === 'general' && <EventForm eventId={selectedEvent || undefined} />}
          {activeTab === 'ticketing' && selectedEvent !== 'new' && <TicketingSettings />}
          {activeTab === 'registration' && selectedEvent !== 'new' && <CustomFields />}
          {activeTab === 'qr-flyer' && selectedEvent !== 'new' && <QrPdfSettings />}
          {activeTab === 'badges' && selectedEvent !== 'new' && <BadgeTemplates />}
          {activeTab === 'stickers' && selectedEvent !== 'new' && <StickerTemplates />}
        </div>
        
      </div>
    </Layout>
  );
};

export default EventSettings;
